import { useTranslation } from 'react-i18next';
import type { Controller, ControllerInspectionData, DoubleCheckInspection, ReportStyleSettings } from './types';
import type { PhotoGeo, PhotoGeoMap } from '../../lib/photoLocation';

interface ReportPhotoIndexProps {
  controllers: Controller[];
  controllerInspectionsData: ControllerInspectionData[];
  doubleCheckInspections: DoubleCheckInspection[];
  styleSettings: ReportStyleSettings;
}

interface PhotoEntry {
  id: string;
  url: string;
  label: string;
  subLabel?: string;
  /** Where this photo was taken, when we captured it (fe9ec2ad). */
  geo?: PhotoGeo | null;
}

/**
 * Google Maps link for a capture point. `q=lat,lng` drops a pin at the exact
 * coordinate and opens in Google Earth / the Maps app on a phone, which is what
 * the client asked for. Six decimals is ~0.1m — well past what a phone GPS
 * resolves, so no precision is lost by trimming.
 */
function mapsUrl(geo: PhotoGeo): string {
  return `https://maps.google.com/?q=${geo.lat.toFixed(6)},${geo.lng.toFixed(6)}`;
}

function collectAllPhotos(
  controllers: Controller[],
  controllerInspectionsData: ControllerInspectionData[],
  doubleCheckInspections: DoubleCheckInspection[],
  t: (key: string, options?: Record<string, unknown>) => string,
): PhotoEntry[] {
  const photos: PhotoEntry[] = [];

  controllers.forEach((controller) => {
    controller.zone_inspections.forEach((zone) => {
      const uniqueUrls = new Set<string>();
      const workCodesLabel = zone.issues && zone.issues.length > 0
        ? zone.issues.map(issue => issue.work_code).join(', ')
        : zone.work_codes || t('report.photoIndexInspection');

      // Collect the capture locations alongside the urls. A photo may appear via
      // an issue or at zone level, so merge both maps and look up by url later.
      const geoByUrl: PhotoGeoMap = {};
      if (zone.issues && zone.issues.length > 0) {
        zone.issues.forEach(issue => {
          issue.photo_urls.forEach(url => uniqueUrls.add(url));
          Object.assign(geoByUrl, issue.photo_geo ?? {});
        });
      } else if (zone.photo_urls) {
        zone.photo_urls.forEach(url => uniqueUrls.add(url));
        Object.assign(geoByUrl, zone.photo_geo ?? {});
      }

      Array.from(uniqueUrls).forEach((url, idx) => {
        photos.push({
          id: `zone-${zone.id}-${idx}`,
          url,
          label: t('report.photoIndexZoneLabel', { n: controller.controller_number, z: zone.zone_number }),
          subLabel: workCodesLabel,
          geo: geoByUrl[url] ?? null,
        });
      });
    });

    const ci = controllerInspectionsData.find(c => c.controller_id === controller.id);
    if (ci && ci.photo_urls.length > 0) {
      ci.photo_urls.forEach((url, idx) => {
        photos.push({
          id: `ctrl-${controller.id}-${idx}`,
          url,
          label: t('report.photoIndexControllerIssue', { n: controller.controller_number }),
          subLabel: ci.failure_codes.length > 0 ? ci.failure_codes.join(', ') : undefined,
          geo: ci.photo_geo?.[url] ?? null,
        });
      });
    }
  });

  doubleCheckInspections.forEach((dc) => {
    dc.photo_urls.forEach((url, idx) => {
      photos.push({
        id: `dc-${dc.id}-${idx}`,
        url,
        label: `${dc.description} (${dc.size}")`,
        subLabel: dc.failure_codes.length > 0 ? dc.failure_codes.join(', ') : t('report.statusNoIssues'),
        geo: dc.photo_geo?.[url] ?? null,
      });
    });
  });

  return photos;
}

export function ReportPhotoIndex({
  controllers,
  controllerInspectionsData,
  doubleCheckInspections,
  styleSettings,
}: ReportPhotoIndexProps) {
  const { t } = useTranslation('inspection');
  const photos = collectAllPhotos(controllers, controllerInspectionsData, doubleCheckInspections, t);

  if (photos.length === 0) return null;

  return (
    <div className="report-photo-index">
      <h2 className="report-section-title" style={{ color: styleSettings.section_heading_color }}>
        {t('report.photoIndexTitle')}
      </h2>
      <div className="report-photo-grid">
        {photos.map((photo) => (
          <div key={photo.id} className="report-photo-card">
            <div className="report-photo-card-label">
              <span className="report-photo-card-title">{photo.label}</span>
              {photo.subLabel && (
                <span className="report-photo-card-sub">{photo.subLabel}</span>
              )}
            </div>
            <img
              src={photo.url}
              alt={photo.label}
              className="report-photo-card-img"
              onClick={() => window.open(photo.url, '_blank')}
            />
            {/* Only photos captured after v1.5.46 carry a location; anything
                older simply has no link rather than a misleading one. */}
            {photo.geo && (
              <a
                href={mapsUrl(photo.geo)}
                target="_blank"
                rel="noopener noreferrer"
                className="report-photo-card-map"
              >
                {t('report.photoIndexViewOnMap')}
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
